/* eslint-disable jsx-a11y/role-has-required-aria-props */
import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Input } from '@lightspeed/flame/Input';
import { InputGroup, InputGroupAddon } from '@lightspeed/flame/InputGroup';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Select } from '@lightspeed/flame/Select';
import { Text } from '@lightspeed/flame/Text';

import Localize from 'app/src/localize/Localize';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import Decimal from 'root/apps/app/src/data/base/Decimal';
import CommonData from 'root/apps/app/src/data/CommonData';
import Format from 'root/apps/app/src/data/Format';
import CCTerminal from 'root/apps/app/src/data/models/CCTerminal';
import { createReactRenderer } from 'root/apps/helpers/react-renderer';

export const TerminalSelectionModal = ({
  paymentProvider,
  onHide,
  amount,
  onStartPayment,
}: {
  paymentProvider: PaymentProvider;
  onHide: () => void;
  amount: Decimal;
  onStartPayment: (selectedTerminal: CCTerminal) => void;
}) => {
  const defaultSelection = 'select-terminal';
  const [isOpen, setIsOpen] = React.useState(true);
  const [selectedTerminal, setSelectedTerminal] = React.useState<CCTerminal>();
  const [selectedTerminalId, setSelectedTerminalId] = React.useState<string>(defaultSelection);

  React.useEffect(() => {
    if (CommonData.selectedRegister) {
      const savedTerminal = CommonData.selectedRegister.get('ccTerminalID');
      if (savedTerminal !== selectedTerminalId) {
        setSelectedTerminalId(savedTerminal);
        setSelectedTerminal(paymentProvider.getTerminal(savedTerminal));
      }
    }
  }, [paymentProvider, selectedTerminalId, setSelectedTerminalId]);

  const handleCloseModal = () => {
    setIsOpen(false);
    onHide();
  };

  const handleTerminalSelected = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const terminalID: string = e.currentTarget.value;
    CommonData.selectedRegister.set('ccTerminalID', terminalID).save();
    setSelectedTerminal(paymentProvider.getTerminal(terminalID));
    setSelectedTerminalId(terminalID);
  };
  const handleStartPayment = () => {
    onStartPayment(selectedTerminal as CCTerminal);
  };

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader showCloseButton={false} onCloseClicked={handleCloseModal} aria-label="heading">
        {Localize.translate('Card Payment')}
      </ModalHeader>
      <ModalBody>
        <Box width="402px">
          <Text fontWeight="bold" aria-label="sub heading">
            {Localize.translate('Terminal')}
          </Text>
          <Select
            id="terminal_select"
            aria-label="terminal selection"
            value={selectedTerminalId}
            onChange={handleTerminalSelected}
          >
            <option
              data-test="option"
              value="select-terminal"
              key="select-terminal"
              aria-label="select-terminal"
            >
              {Localize.translate('Select a Terminal')}
            </option>
            {paymentProvider.terminalCollection.map((terminal: CCTerminal) => (
              <option
                value={terminal.id}
                data-test="option"
                key={terminal.id}
                aria-label={`${terminal.get('name')}-terminal`}
              >
                {terminal.get('name')}
              </option>
            ))}
          </Select>
          <Box flexDirection="column" mt={2}>
            <label htmlFor="amount">
              <Text size="small" fontWeight="bold" mb={1}>
                {Localize.translate('Amount')}
              </Text>
            </label>
            <InputGroup>
              <InputGroupAddon>{Localize.getCurrencySymbol()} </InputGroupAddon>
              <Input
                id="amount"
                name="amount"
                aria-label="amount"
                value={Format.formatMoney(amount, 0, false)}
                disabled
              />
            </InputGroup>
          </Box>
          <Divider mt={4} mb={4} />
          <Flex justifyContent="space-between" mb={2}>
            <Text size="small" fontWeight="bold" aria-label="balance heading">
              {Localize.translate('Balance')}
            </Text>
            <Text size="small" aria-label="balance amount">
              {Format.formatMoney(amount, 0)}
            </Text>
          </Flex>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button mr="2" onClick={handleCloseModal} aria-label="cancel">
            {Localize.translate('Cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={selectedTerminalId === defaultSelection}
            fill
            role="button"
            onClick={handleStartPayment}
            aria-label="start payment button"
          >
            {Localize.translate('Start Payment')}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
export function selectTerminal({
  paymentProvider,
  amount,
}: {
  paymentProvider: PaymentProvider;
  amount: Decimal;
}) {
  return new Promise<CCTerminal>((resolve, reject) => {
    //  @ts-ignore no-unused-variable
    let internalTerminalSelectionModal = null;

    const RenderedComponent = createReactRenderer({
      component: TerminalSelectionModal,
      selector: '[data-react-root="ReactModal"]',
      props: {
        onHide: () => {
          internalTerminalSelectionModal = null;
          RenderedComponent.unmount();
          reject();
        },
        onStartPayment: (selectedTerminal: CCTerminal) => {
          resolve(selectedTerminal);
        },
        paymentProvider,
        amount,
      },
    });
    internalTerminalSelectionModal = RenderedComponent.mount();
  });
}
